'use client';

/**
 * The character-tile prompt — one big tile per key, for key-drill lessons.
 *
 * A wall of monospaced text is the wrong shape for a five-year-old learning
 * where "F" lives. What they need is ONE character, large, with a clear sense
 * of "these are done, this one is now, these are coming".
 *
 * The whole exercise is rendered as a filmstrip and the strip is *translated*
 * so the active tile sits exactly at the horizontal centre of the stage. That
 * matters more than it sounds: the child's eyes should rest at one fixed point
 * and let the letters come to them, rather than tracking left-to-right and
 * re-finding the cursor after every keystroke. At the very start the left half
 * is simply empty — the tile you are about to type is still dead centre.
 *
 * Rendering every character rather than a sliding window keeps the motion
 * smooth: the DOM never changes, only the transform, so there is nothing for a
 * window-shift to fight with mid-transition. Key drills top out around 160
 * characters, and the tiles are memoised, so the cost is negligible.
 */
import React, { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CharState } from '../types';

/** useLayoutEffect warns during SSR; the measurement only exists client-side. */
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function Tile({ char, state }: { char: string; state: CharState }) {
  const isSpace = char === ' ';

  return (
    <span className={`k-tile k-tile-${state}${isSpace ? ' k-tile-space' : ''}`} aria-hidden="true">
      {isSpace ? <span className="k-tile-space-mark">␣</span> : char.toUpperCase()}
    </span>
  );
}

const MemoTile = memo(Tile);

export interface PromptTilesProps {
  target: string;
  states: CharState[];
  cursor: number;
}

function PromptTiles({ target, states, cursor }: PromptTilesProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [shift, setShift] = useState<number | null>(null);

  /** Offset the strip so the active tile's midpoint meets the viewport's. */
  const centreActive = useCallback(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    const index = Math.min(cursor, target.length - 1);
    const active = track.children[index] as HTMLElement | undefined;
    if (!active) return;

    setShift(viewport.clientWidth / 2 - (active.offsetLeft + active.offsetWidth / 2));
  }, [cursor, target.length]);

  useIsomorphicLayoutEffect(centreActive, [centreActive]);

  // Tile widths are viewport-relative (clamp), so a resize moves the centre.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(centreActive);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [centreActive]);

  return (
    <div className="k-tiles-viewport" ref={viewportRef} role="group" aria-label="Keys to type">
      <div
        className="k-tiles-track"
        ref={trackRef}
        style={{
          transform: `translateX(${shift ?? 0}px)`,
          // Hidden until the first measurement lands, so the strip is never
          // seen sitting at the wrong offset for a frame.
          visibility: shift === null ? 'hidden' : 'visible',
        }}
      >
        {target.split('').map((char, i) => (
          <MemoTile key={i} char={char} state={states[i]} />
        ))}
      </div>
    </div>
  );
}

export default memo(PromptTiles);
